/**
 * 积分（积分）充值：GET /asset/score-skus + 下单
 */

import api from '@/utils/api'
import { getApiErrorMessage, unwrapOrderPayload } from './vipSkuApi'

function assertApiOk(res) {
  if (res == null || typeof res !== 'object') return
  const c = res.code
  if (c === undefined || c === null) return
  const n = Number(c)
  if (n === 0 || n === 200) return
  const msg = res.message || res.msg || res.error || `接口 code=${c}`
  throw new Error(msg)
}

function unwrapData(res) {
  if (res == null) return null
  if (res.data != null && typeof res.data === 'object' && !Array.isArray(res.data)) {
    return res.data
  }
  return res
}

function unwrapScoreSkuList(res) {
  if (res == null) return []
  if (Array.isArray(res)) return res
  if (Array.isArray(res.list)) return res.list
  const d = res.data
  if (Array.isArray(d)) return d
  if (d && typeof d === 'object') {
    if (Array.isArray(d.list)) return d.list
    if (Array.isArray(d.data)) return d.data
  }
  return []
}

function priceYuanFromCents(cents) {
  const n = Number(cents)
  if (!Number.isFinite(n) || n <= 0) return 0
  if (n >= 100) return Math.round(n / 100)
  return Math.round(n)
}

/** 接口无数据时的占位档位 */
export const DEFAULT_CREDIT_PACKS = [
  { id: 'default-490', skuId: null, points: 490, price: 7 },
  { id: 'default-1400', skuId: null, points: 1400, price: 20 },
  { id: 'default-2100', skuId: null, points: 2100, price: 30 },
  { id: 'default-3500', skuId: null, points: 3500, price: 50 },
  { id: 'default-7000', skuId: null, points: 7000, price: 100 },
  { id: 'default-14000', skuId: null, points: 14000, price: 200 },
  {
    id: 'default-84000',
    skuId: null,
    points: 84000,
    pointsOld: 70000,
    price: 1000,
    memberBonus: '20%',
    featured: true
  }
]

/**
 * 将 /asset/score-skus 单项映射为充值档位
 * @param {object} sku
 */
export function mapScoreSkuToCreditPack(sku) {
  const points = Number(sku.score ?? sku.reward_scores ?? sku.points) || 0
  const price = priceYuanFromCents(sku.discount_money_price ?? sku.money_price)
  const oldPoints =
    sku.huaxian_score != null
      ? Number(sku.huaxian_score)
      : sku.line_score != null
        ? Number(sku.line_score)
        : null
  const bonus = sku.hottitle || sku.hot_title || sku.member_bonus || sku.bonus_tip
  return {
    id: `score-sku-${sku.id}`,
    skuId: sku.id,
    points,
    pointsOld: oldPoints && oldPoints > points ? oldPoints : null,
    price,
    memberBonus: bonus ? String(bonus) : null,
    featured: !!(sku.is_featured || sku.is_large || Number(sku.list_weight) >= 98),
    thumb: sku.thumb || null
  }
}

/**
 * 拉取积分充值档位 GET /asset/score-skus
 */
export async function fetchCreditRechargePacks(opts = {}) {
  const params = {
    page: opts.page ?? 1,
    limit: opts.limit ?? 50,
    ...(opts.extra || {})
  }
  const res = await api.get('/asset/score-skus', { params })
  assertApiOk(res)
  const list = unwrapScoreSkuList(res)
  if (list.length) {
    return list
      .map(mapScoreSkuToCreditPack)
      .filter(p => p.points > 0)
      .sort((a, b) => {
        const wa = Number(a.price) || 0
        const wb = Number(b.price) || 0
        return wa - wb
      })
  }
  return DEFAULT_CREDIT_PACKS.map(p => ({ ...p }))
}

/**
 * 积分充值下单 POST /asset/score-order/confirm
 * @param {{ skuId: number|string }} opts
 */
export async function createScoreOrder(opts = {}) {
  const skuId = Number(opts.skuId)
  if (!Number.isFinite(skuId) || skuId <= 0) {
    throw new Error('请选择充值档位')
  }
  const res = await api.post('/asset/score-order/confirm', {
    sku_id: skuId,
    payment_method: 'alipay',
    paymentMethod: 'alipay',
    pay_type: 'alipay'
  })
  assertApiOk(res)
  return unwrapOrderPayload(res)
}

/** 会员等级色标 class（与余额弹窗一致） */
export function getVipnameBadgeClass(vipname, isMember = false) {
  const name = String(vipname || 'FREE').trim().toLowerCase()
  if (/free|免费|工作室/.test(name)) return 'crm-vip-badge--free'
  if (/标准|standard|基础|basic/.test(name)) return 'crm-vip-badge--standard'
  if (/专业|pro|高级|plus/.test(name)) return 'crm-vip-badge--pro'
  if (/旗舰|ultra|max|尊享|ultimate/.test(name)) return 'crm-vip-badge--flagship'
  if (isMember) return 'crm-vip-badge--member'
  return 'crm-vip-badge--free'
}

export function formatVipEndAt(s) {
  if (!s) return '—'
  const d = new Date(s)
  if (Number.isNaN(d.getTime())) {
    const raw = String(s).trim()
    return raw.length >= 10 ? raw.slice(0, 10) : raw
  }
  const pad = x => String(x).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** 解析 /user 为弹窗展示字段 */
export function mapUserToCreditsProfile(res) {
  const u =
    (res && res.data && res.data.user) ||
    (res && res.user) ||
    (res && res.data) ||
    {}
  const name = u.name || u.nickname || u.nicheng || '用户'
  const avatar = u.headimg || u.avatar || u.thumb || ''
  const score = Number(u.score ?? u.credits ?? u.balance ?? 0) || 0
  const vip =
    u.viplevel ||
    u.vip_level ||
    u.member_level ||
    (u.is_vip || u.is_member ? '会员' : null)
  const levelLabel = vip ? String(vip) : 'FREE 工作室'
  const isMember = !!(u.is_vip || u.is_member || (vip && !/free/i.test(String(vip))))
  const vipnameRaw = u.vipname ?? u.vip_name
  const vipname =
    vipnameRaw != null && String(vipnameRaw).trim()
      ? String(vipnameRaw).trim()
      : levelLabel || 'FREE'
  const vipEndAt = u.vip_end_at ?? u.vip_end_time ?? u.vip_expire_at ?? ''
  return {
    name,
    avatar,
    score,
    levelLabel,
    vipname,
    vipEndAt: vipEndAt != null ? String(vipEndAt) : '',
    isMember
  }
}

export async function fetchCreditsUserProfile() {
  const res = await api.get('/user')
  return mapUserToCreditsProfile(res)
}

export { getApiErrorMessage }
